import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  League,
  Invite,
  LeaderboardEntry,
  FlashCardSetSummary,
  LeagueManager,
  ManagerInvite,
  PlayerSearchResult,
  getLeague,
  getLeaderboard,
  listInvites,
  createInvite,
  cancelInvite,
  listFlashCardSets,
  deleteFlashCardSet,
  updateLeague,
  listLeagueManagers,
  inviteManager,
  listManagerInvites,
  cancelManagerInvite,
  removeManager,
  searchPlayerForLeague,
  addPlayerToLeague,
} from '../../api/leagues';
import '../../styles/global.css';

const LeagueDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { leagueId } = useParams<{ leagueId: string }>();
  const [league, setLeague] = useState<League | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [flashCardSets, setFlashCardSets] = useState<FlashCardSetSummary[]>([]);
  const [managers, setManagers] = useState<LeagueManager[]>([]);
  const [managerInvites, setManagerInvites] = useState<ManagerInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState((location.state as any)?.success || '');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');

  // Player invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Add existing player form
  const [playerSearch, setPlayerSearch] = useState('');
  const [searchResult, setSearchResult] = useState<PlayerSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addSuccess, setAddSuccess] = useState('');

  // Manager invite form
  const [mgrInviteEmail, setMgrInviteEmail] = useState('');
  const [sendingMgrInvite, setSendingMgrInvite] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('manager_token');
      if (!token || !leagueId) {
        navigate('/manager');
        return;
      }

      localStorage.setItem('token', token);
      try {
        const [leagueData, leaderboardData, invitesData, flashCardSetsData, managersData, mgrInvitesData] = await Promise.all([
          getLeague(leagueId),
          getLeaderboard(leagueId),
          listInvites(leagueId),
          listFlashCardSets(leagueId),
          listLeagueManagers(leagueId),
          listManagerInvites(leagueId),
        ]);
        setLeague(leagueData);
        setLeaderboard(leaderboardData.entries);
        setInvites(invitesData);
        setFlashCardSets(flashCardSetsData);
        setManagers(managersData);
        setManagerInvites(mgrInvitesData);
      } catch (err) {
        setError('Failed to load league data');
      } finally {
        localStorage.removeItem('token');
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate, leagueId]);

  const withToken = async (fn: () => Promise<void>) => {
    const token = localStorage.getItem('manager_token');
    localStorage.setItem('token', token || '');
    try {
      await fn();
    } finally {
      localStorage.removeItem('token');
    }
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leagueId) return;

    setError('');
    setInviteSuccess('');
    setSendingInvite(true);

    await withToken(async () => {
      try {
        const newInvite = await createInvite(leagueId, inviteEmail);
        setInvites([newInvite, ...invites]);
        setInviteEmail('');
        setInviteSuccess(`Invite sent to ${newInvite.email}`);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to send invite');
      }
    });
    setSendingInvite(false);
  };

  const handleSearchPlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leagueId || !playerSearch.trim()) return;

    setError('');
    setAddSuccess('');
    setSearchResult(null);
    setSearching(true);

    await withToken(async () => {
      try {
        const result = await searchPlayerForLeague(leagueId, playerSearch.trim());
        setSearchResult(result);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to search for player');
      }
    });
    setSearching(false);
  };

  const handleAddPlayer = async () => {
    if (!leagueId || !searchResult?.player_id) return;

    setError('');
    setAdding(true);

    await withToken(async () => {
      try {
        await addPlayerToLeague(leagueId, searchResult.player_id!);
        setAddSuccess(`${searchResult.username} has been added to ${league?.name}!`);
        setPlayerSearch('');
        setSearchResult(null);

        // Refresh leaderboard
        const leaderboardData = await getLeaderboard(leagueId);
        setLeaderboard(leaderboardData.entries);
      } catch (err: any) {
        if (err.response?.status === 409) {
          setError('This player is already in a league.');
        } else {
          setError(err.response?.data?.detail || 'Failed to add player');
        }
      }
    });
    setAdding(false);
  };

  const handleSendManagerInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leagueId) return;

    setError('');
    setInviteSuccess('');
    setSendingMgrInvite(true);

    await withToken(async () => {
      try {
        const newInvite = await inviteManager(leagueId, mgrInviteEmail);
        setManagerInvites([newInvite, ...managerInvites]);
        setMgrInviteEmail('');
        setInviteSuccess(`Manager invite sent to ${newInvite.email}`);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to send manager invite');
      }
    });
    setSendingMgrInvite(false);
  };

  const handleDeleteFlashCardSet = async (categoryId: string, setName: string) => {
    if (!leagueId) return;
    if (!window.confirm(`This will also remove "${setName}" from any players it's assigned to. Delete?`)) return;

    await withToken(async () => {
      try {
        await deleteFlashCardSet(leagueId, categoryId);
        setFlashCardSets(flashCardSets.filter(s => s.id !== categoryId));
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to delete flash card set');
      }
    });
  };

  const handleSaveName = async () => {
    if (!leagueId || !nameInput.trim() || nameInput.trim() === league?.name) {
      setEditingName(false);
      return;
    }
    await withToken(async () => {
      try {
        const updated = await updateLeague(leagueId, nameInput.trim());
        setLeague(updated);
        setEditingName(false);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to update league name');
      }
    });
  };

  const handleCancelInvite = async (inviteId: string) => {
    if (!leagueId) return;
    await withToken(async () => {
      try {
        await cancelInvite(leagueId, inviteId);
        setInvites(invites.filter(i => i.id !== inviteId));
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to cancel invite');
      }
    });
  };

  const handleCancelManagerInvite = async (inviteId: string) => {
    if (!leagueId) return;
    await withToken(async () => {
      try {
        await cancelManagerInvite(leagueId, inviteId);
        setManagerInvites(managerInvites.filter(i => i.id !== inviteId));
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to cancel manager invite');
      }
    });
  };

  const handleRemoveManager = async (managerId: string, managerName: string) => {
    if (!leagueId) return;
    if (!window.confirm(`Remove ${managerName} as a manager of this league?`)) return;
    await withToken(async () => {
      try {
        await removeManager(leagueId, managerId);
        setManagers(managers.filter(m => m.manager_id !== managerId));
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to remove manager');
      }
    });
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!league) {
    return <div className="error">League not found</div>;
  }

  const isOwner = league.role === 'owner';
  const pendingInvites = invites.filter(i => i.status === 'pending');

  return (
    <div className="page">
      <nav className="navbar">
        <div className="nav-brand">
          <button
            className="link-btn"
            onClick={() => navigate('/manager/dashboard')}
            style={{ marginRight: '10px' }}
          >
            &larr; Back
          </button>
          {editingName ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') setEditingName(false);
                }}
                autoFocus
                style={{ fontSize: '16px', padding: '2px 6px', width: '200px' }}
              />
              <button className="btn btn-primary" onClick={handleSaveName}
                style={{ fontSize: '11px', padding: '2px 8px' }}>Save</button>
              <button className="btn" onClick={() => setEditingName(false)}
                style={{ fontSize: '11px', padding: '2px 8px' }}>Cancel</button>
            </span>
          ) : (
            <span
              onClick={() => { setNameInput(league.name); setEditingName(true); }}
              style={{ cursor: 'pointer' }}
              title="Click to edit league name"
            >
              {league.name} <span style={{ fontSize: '12px', color: '#999' }}>&#9998;</span>
            </span>
          )}
        </div>
        <div className="nav-links">
          <button className="nav-btn" onClick={() => navigate('/manager/help')}>
            Help
          </button>
        </div>
      </nav>

      <div className="container">
        {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}
        {(inviteSuccess || successMsg) && (
          <div className="success" style={{ marginBottom: '20px' }}>{inviteSuccess || successMsg}</div>
        )}

        {/* Player Invites Section */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3>Player Invites</h3>
          <p className="description" style={{ marginBottom: '15px' }}>
            Invite players to join your league. They will receive an email with a link to join.
          </p>
          <form onSubmit={handleSendInvite} style={{ display: 'flex', gap: '10px' }}>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Enter player's email"
              required
              style={{ flex: 1 }}
            />
            <button type="submit" className="btn btn-primary" disabled={sendingInvite}>
              {sendingInvite ? 'Sending...' : 'Send Invite'}
            </button>
          </form>

          {pendingInvites.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>
                Pending Invites ({pendingInvites.length})
              </h4>
              {pendingInvites.map((invite) => (
                <div
                  key={invite.id}
                  style={{
                    padding: '10px 0',
                    borderBottom: '1px solid #eee'
                  }}
                >
                  <div>
                    <span>{invite.email}</span>
                    <span style={{ color: '#999', fontSize: '12px', marginLeft: '10px' }}>
                      Expires: {new Date(invite.expires_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div style={{ marginTop: '6px' }}>
                    <code style={{ fontSize: '11px', color: '#666', wordBreak: 'break-all' }}>
                      {invite.invite_url}
                    </code>
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                    <button
                      className="btn"
                      onClick={() => {
                        navigator.clipboard.writeText(invite.invite_url);
                      }}
                      style={{ fontSize: '11px', padding: '4px 10px' }}
                    >
                      Copy Link
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleCancelInvite(invite.id)}
                      style={{ fontSize: '11px', padding: '4px 10px' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Existing Player Section */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3>Add Existing Player</h3>
          <p className="description" style={{ marginBottom: '15px' }}>
            Search for an existing player by username to add them to your league.
          </p>
          <form onSubmit={handleSearchPlayer} style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              value={playerSearch}
              onChange={(e) => setPlayerSearch(e.target.value)}
              placeholder="Enter username"
              required
              style={{ flex: 1 }}
            />
            <button type="submit" className="btn btn-primary" disabled={searching}>
              {searching ? 'Searching...' : 'Search'}
            </button>
          </form>

          {addSuccess && (
            <div className="success" style={{ marginTop: '10px' }}>{addSuccess}</div>
          )}

          {searching && (
            <p style={{ marginTop: '10px', color: '#999' }}>Searching...</p>
          )}

          {searchResult && !searching && (
            <div style={{ marginTop: '15px' }}>
              {!searchResult.found && (
                <p style={{ color: '#999' }}>No player found with that username.</p>
              )}
              {searchResult.found && searchResult.has_league && (
                <p style={{ color: '#e65100' }}>This player is already in a league.</p>
              )}
              {searchResult.found && !searchResult.has_league && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                }}>
                  <span>
                    <strong>{searchResult.username}</strong>
                    {searchResult.grade !== undefined && searchResult.grade !== null && (
                      <span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
                        {searchResult.grade === 0 ? 'Kindergarten' : `Grade ${searchResult.grade}`}
                      </span>
                    )}
                  </span>
                  <button
                    className="btn btn-primary"
                    onClick={handleAddPlayer}
                    disabled={adding}
                    style={{ fontSize: '12px' }}
                  >
                    {adding ? 'Adding...' : 'Add to League'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Managers Section */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3>Managers ({managers.length})</h3>
          <div style={{ marginTop: '10px' }}>
            {managers.map((mgr) => (
              <div
                key={mgr.manager_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid #eee',
                  gap: '8px',
                }}
              >
                <span style={{ flex: 1 }}>
                  <strong>{mgr.name}</strong>
                  <span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
                    {mgr.email}
                  </span>
                  <span style={{
                    fontSize: '10px',
                    padding: '1px 6px',
                    borderRadius: '8px',
                    marginLeft: '8px',
                    backgroundColor: mgr.role === 'owner' ? '#e3f2fd' : '#f3e5f5',
                    color: mgr.role === 'owner' ? '#1565c0' : '#7b1fa2',
                    fontWeight: 600,
                  }}>
                    {mgr.role === 'owner' ? 'Owner' : 'Manager'}
                  </span>
                </span>
                {isOwner && mgr.role !== 'owner' && (
                  <button
                    className="btn btn-danger"
                    onClick={() => handleRemoveManager(mgr.manager_id, mgr.name)}
                    style={{ fontSize: '11px', padding: '2px 8px' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Invite Manager Form */}
          <div style={{ marginTop: '15px' }}>
            <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Invite Co-Manager</h4>
            <form onSubmit={handleSendManagerInvite} style={{ display: 'flex', gap: '10px' }}>
              <input
                type="email"
                value={mgrInviteEmail}
                onChange={(e) => setMgrInviteEmail(e.target.value)}
                placeholder="Enter manager's email"
                required
                style={{ flex: 1 }}
              />
              <button type="submit" className="btn btn-primary" disabled={sendingMgrInvite}
                style={{ fontSize: '12px' }}>
                {sendingMgrInvite ? 'Sending...' : 'Invite'}
              </button>
            </form>
          </div>

          {/* Pending Manager Invites */}
          {managerInvites.length > 0 && (
            <div style={{ marginTop: '15px' }}>
              <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>
                Pending Manager Invites ({managerInvites.length})
              </h4>
              {managerInvites.map((inv) => (
                <div
                  key={inv.id}
                  style={{
                    padding: '10px 0',
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <div>
                    <span>{inv.email}</span>
                    <span style={{ color: '#999', fontSize: '12px', marginLeft: '10px' }}>
                      Expires: {new Date(inv.expires_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div style={{ marginTop: '6px' }}>
                    <code style={{ fontSize: '11px', color: '#666', wordBreak: 'break-all' }}>
                      {inv.invite_url}
                    </code>
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                    <button
                      className="btn"
                      onClick={() => {
                        navigator.clipboard.writeText(inv.invite_url);
                      }}
                      style={{ fontSize: '11px', padding: '4px 10px' }}
                    >
                      Copy Link
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleCancelManagerInvite(inv.id)}
                      style={{ fontSize: '11px', padding: '4px 10px' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Custom Flash Card Sets */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3>Custom Flash Card Sets ({flashCardSets.length})</h3>
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/manager/leagues/${leagueId}/flash-card-sets/new`)}
            style={{ fontSize: '12px', padding: '6px 12px', marginTop: '10px' }}
          >
            Create Flash Card Set
          </button>
          {flashCardSets.length === 0 ? (
            <p className="placeholder-text" style={{ marginTop: '10px' }}>
              No custom flash card sets yet. Create one to assign to your players.
            </p>
          ) : (
            <div style={{ marginTop: '10px' }}>
              {flashCardSets.map((set) => (
                <div
                  key={set.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 0',
                    borderBottom: '1px solid #eee',
                    gap: '8px',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong>{set.name}</strong>
                    <span style={{ color: '#999', fontSize: '12px', marginLeft: '10px' }}>
                      D{set.difficulty} &middot; {set.card_count} cards
                    </span>
                  </span>
                  <button
                    className="btn"
                    onClick={() => navigate(`/manager/leagues/${leagueId}/categories/${set.id}/cards`)}
                    style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      flexShrink: 0,
                      width: '42px',
                      lineHeight: '18px',
                    }}
                  >
                    View
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDeleteFlashCardSet(set.id, set.name)}
                    style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      flexShrink: 0,
                      width: '50px',
                      lineHeight: '18px',
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div className="card">
          <h3>Leaderboard</h3>
          {leaderboard.length === 0 ? (
            <p className="placeholder-text" style={{ marginTop: '10px' }}>
              No players yet. Send invites to get started!
            </p>
          ) : (
            <table style={{ width: '100%', marginTop: '15px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee' }}>
                  <th style={{ textAlign: 'left', padding: '10px 5px' }}>Rank</th>
                  <th style={{ textAlign: 'left', padding: '10px 5px' }}>Player</th>
                  <th style={{ textAlign: 'right', padding: '10px 5px' }}>Total XP</th>
                  <th style={{ textAlign: 'right', padding: '10px 5px' }}></th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry) => (
                  <tr key={entry.player_id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px 5px' }}>
                      {entry.rank === 1 && '\u{1F947} '}
                      {entry.rank === 2 && '\u{1F948} '}
                      {entry.rank === 3 && '\u{1F949} '}
                      {entry.rank}
                    </td>
                    <td style={{ padding: '10px 5px' }}>{entry.player_username}</td>
                    <td style={{ textAlign: 'right', padding: '10px 5px' }}>
                      {entry.total_xp.toLocaleString()} XP
                    </td>
                    <td style={{ textAlign: 'right', padding: '10px 5px' }}>
                      <button
                        className="btn"
                        onClick={() => navigate(`/manager/leagues/${leagueId}/players/${entry.player_id}/categories`)}
                        style={{ fontSize: '12px', padding: '4px 10px' }}
                      >
                        Flash Card Sets
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeagueDetail;
